import React, { Component, Fragment, ReactNode } from "react";
import Table from "react-bootstrap/Table";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Select from "react-select";
import { ChecklistFieldDef } from "./DynamicChecklistManagement";
import {
  DuplicateTargetOption,
  fieldKeyMatchesScope,
  fieldSourceKey,
} from "./duplicateFieldKeys";

export interface MasterFieldDuplicateRowContext {
  field: ChecklistFieldDef;
  index: number;
  sourceKey: string;
  selectionCell: React.ReactNode;
  openSingleDuplicatePicker: () => void;
}

interface Props {
  sectionId: string;
  groupId?: string;
  fields: ChecklistFieldDef[];
  colSpan: number;
  enumerateTargets: (sourceKeys: string[]) => DuplicateTargetOption[];
  onDuplicate: (sourceKeys: string[], targetKey: string) => void;
  renderRow: (ctx: MasterFieldDuplicateRowContext) => ReactNode;
  /** When set, the parent supplies the table chrome (thead/tbody wrapper). */
  renderChrome?: (api: MasterFieldDuplicateTableChrome) => ReactNode;
}

export interface MasterFieldDuplicateTableChrome {
  renderSelectAllHeader: () => ReactNode;
  renderBody: () => ReactNode;
}

interface State {
  selectedKeys: string[];
  singlePickerKey: string | null;
  duplicateTargetKey: string;
}

const DUP_SELECT_STYLES = {
  control: (base: Record<string, unknown>) => ({
    ...base,
    minHeight: 31,
    fontSize: "0.875rem",
  }),
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 }),
};

/**
 * Isolated duplicate-selection state for one checklist field table so checkbox
 * toggles do not re-render the entire management page.
 */
export class MasterFieldDuplicateTable extends React.PureComponent<Props, State> {
  state: State = {
    selectedKeys: [],
    singlePickerKey: null,
    duplicateTargetKey: "",
  };

  private selectedSet(): Set<string> {
    return new Set(this.state.selectedKeys);
  }

  isSelected = (sourceKey: string): boolean => this.selectedSet().has(sourceKey);

  toggleSelection = (sourceKey: string) => {
    this.setState((prev) => {
      const next = new Set(prev.selectedKeys);
      if (next.has(sourceKey)) next.delete(sourceKey);
      else next.add(sourceKey);
      return {
        selectedKeys: Array.from(next),
        singlePickerKey:
          prev.singlePickerKey && prev.singlePickerKey !== sourceKey
            ? prev.singlePickerKey
            : null,
        duplicateTargetKey:
          prev.singlePickerKey && prev.singlePickerKey !== sourceKey
            ? ""
            : prev.duplicateTargetKey,
      };
    });
  };

  toggleSelectAll = () => {
    const { sectionId, groupId, fields } = this.props;
    const allKeys = fields.map((f) => fieldSourceKey(sectionId, f.id, groupId));
    this.setState((prev) => {
      const selectedInScope = prev.selectedKeys.filter((k) =>
        fieldKeyMatchesScope(k, sectionId, groupId)
      );
      const allSelected =
        allKeys.length > 0 && allKeys.every((k) => selectedInScope.includes(k));
      const next = new Set(prev.selectedKeys);
      if (allSelected) allKeys.forEach((k) => next.delete(k));
      else allKeys.forEach((k) => next.add(k));
      return { selectedKeys: Array.from(next) };
    });
  };

  clearSelection = () => {
    const { sectionId, groupId } = this.props;
    this.setState((prev) => ({
      selectedKeys: prev.selectedKeys.filter(
        (k) => !fieldKeyMatchesScope(k, sectionId, groupId)
      ),
      duplicateTargetKey: "",
    }));
  };

  openSinglePicker = (sourceKey: string) => {
    this.setState((prev) => ({
      singlePickerKey: prev.singlePickerKey === sourceKey ? null : sourceKey,
      duplicateTargetKey: "",
    }));
  };

  closePickers = () => {
    this.setState({
      singlePickerKey: null,
      duplicateTargetKey: "",
    });
  };

  applyDuplicate = () => {
    const { sectionId, groupId, onDuplicate } = this.props;
    const { duplicateTargetKey } = this.state;
    if (!duplicateTargetKey) return;

    let sourceKeys: string[];
    if (this.state.singlePickerKey) {
      sourceKeys = [this.state.singlePickerKey];
    } else {
      sourceKeys = this.state.selectedKeys.filter((k) =>
        fieldKeyMatchesScope(k, sectionId, groupId)
      );
    }

    if (!sourceKeys.length) return;
    onDuplicate(sourceKeys, duplicateTargetKey);
    this.setState((prev) => ({
      selectedKeys: prev.selectedKeys.filter((k) => !sourceKeys.includes(k)),
      singlePickerKey: null,
      duplicateTargetKey: "",
    }));
  };

  applyBulkDuplicate = () => {
    if (this.state.singlePickerKey) return;
    this.applyDuplicate();
  };

  renderSelectionCell = (sourceKey: string) => (
    <Form.Check
      type="checkbox"
      checked={this.isSelected(sourceKey)}
      onChange={() => this.toggleSelection(sourceKey)}
      aria-label="Select field for bulk duplicate"
      title="Select for bulk duplicate"
    />
  );

  renderSelectAllHeader = () => {
    const { sectionId, groupId, fields } = this.props;
    const allKeys = fields.map((f) => fieldSourceKey(sectionId, f.id, groupId));
    const allSelected = fields.length > 0 && allKeys.every((k) => this.isSelected(k));
    return (
      <Form.Check
        type="checkbox"
        checked={allSelected}
        onChange={this.toggleSelectAll}
        aria-label="Select all fields for bulk duplicate"
      />
    );
  };

  renderDestinationSelect = (sourceKeys: string[], width = 280) => {
    const targets = this.props.enumerateTargets(sourceKeys);
    const noTargets = targets.length === 0;
    return (
      <div style={{ width, minWidth: 220 }}>
        <Select
          classNamePrefix="dvc-rs"
          value={
            targets.find((o) => o.value === this.state.duplicateTargetKey) ?? null
          }
          onChange={(opt) =>
            this.setState({ duplicateTargetKey: opt?.value ?? "" })
          }
          options={targets}
          placeholder={
            noTargets
              ? "Selected field(s) already exist in every other destination"
              : "Choose a destination..."
          }
          isDisabled={noTargets}
          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
          styles={DUP_SELECT_STYLES}
        />
      </div>
    );
  };

  renderPickerRow = (sourceKeys: string[], heading: string) => {
    const { colSpan } = this.props;
    const targets = this.props.enumerateTargets(sourceKeys);
    const noTargets = targets.length === 0;
    return (
      <tr className="dvc-duplicate-picker-row">
        <td colSpan={colSpan} className="p-2 bg-light">
          <div
            className="d-flex align-items-center flex-wrap dvc-section-actions"
            style={{ gap: 8, rowGap: 8 }}
          >
            <span className="small text-muted">{heading}</span>
            {this.renderDestinationSelect(sourceKeys)}
            <Button
              size="sm"
              variant="outline-info"
              style={{ height: 31, minWidth: 90 }}
              disabled={!this.state.duplicateTargetKey || noTargets}
              onClick={this.applyDuplicate}
            >
              Duplicate
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              style={{ height: 31 }}
              onClick={this.closePickers}
            >
              Cancel
            </Button>
          </div>
        </td>
      </tr>
    );
  };

  renderBulkToolbar = () => {
    const { sectionId, groupId, colSpan } = this.props;
    const selected = this.state.selectedKeys.filter((k) =>
      fieldKeyMatchesScope(k, sectionId, groupId)
    );
    if (!selected.length) return null;
    const targets = this.props.enumerateTargets(selected);
    const noTargets = targets.length === 0;
    return (
      <tr className="dvc-duplicate-bulk-toolbar-row">
        <td colSpan={colSpan} className="py-2 px-3 bg-white border-bottom">
          <div className="d-flex align-items-center flex-wrap" style={{ gap: 8, rowGap: 8 }}>
            <span className="small font-weight-bold text-primary">
              {selected.length} field{selected.length === 1 ? "" : "s"} selected
            </span>
            {this.renderDestinationSelect(selected)}
            <Button
              size="sm"
              variant="outline-info"
              style={{ height: 31 }}
              disabled={!this.state.duplicateTargetKey || noTargets}
              onClick={this.applyBulkDuplicate}
            >
              Duplicate selected
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={this.clearSelection}>
              Clear selection
            </Button>
          </div>
        </td>
      </tr>
    );
  };

  renderBodyRows = () => {
    const { sectionId, groupId, fields, renderRow } = this.props;
    const sortedFields = [...fields].sort((a, b) => a.order - b.order);

    return (
      <>
        {this.renderBulkToolbar()}
        {sortedFields.map((field, index) => {
          const sourceKey = fieldSourceKey(sectionId, field.id, groupId);
          const showSinglePicker = this.state.singlePickerKey === sourceKey;
          const ctx: MasterFieldDuplicateRowContext = {
            field,
            index,
            sourceKey,
            selectionCell: this.renderSelectionCell(sourceKey),
            openSingleDuplicatePicker: () => this.openSinglePicker(sourceKey),
          };
          return (
            <Fragment key={field.id}>
              {renderRow(ctx)}
              {showSinglePicker ? this.renderPickerRow([sourceKey], "Duplicate to:") : null}
            </Fragment>
          );
        })}
      </>
    );
  };

  render() {
    const chrome: MasterFieldDuplicateTableChrome = {
      renderSelectAllHeader: () => this.renderSelectAllHeader(),
      renderBody: () => this.renderBodyRows(),
    };

    if (this.props.renderChrome) {
      return <>{this.props.renderChrome(chrome)}</>;
    }

    return (
      <Table responsive size="sm" className="mb-2 bg-light">
        <thead>
          <tr>
            <th className="dvc-col-select" title="Select for bulk duplicate">
              {chrome.renderSelectAllHeader()}
            </th>
            <th>#</th>
            <th>Label</th>
            <th>Type</th>
            <th>Layout</th>
            <th className="text-right" style={{ width: 280 }}>
              Actions
            </th>
          </tr>
        </thead>
        <tbody>{chrome.renderBody()}</tbody>
      </Table>
    );
  }
}
